/**
 * Carbon checkbox (DESIGN.md "Inputs / Fields"): a native 16px checkbox with
 * a 1px ink stroke that fills with ink when checked or mixed, the mark cut
 * out in the ground color. Its 2px corner is the one sanctioned exception to
 * the Square Rule (D20), set inline because arbitrary radius classes are banned (§8.3). Focus
 * draws outside the box. Give it a visible `label`, or an `aria-label` when
 * it stands alone ("Select drift {setting}" in a table row).
 */
import { forwardRef, useEffect, useRef, type ForwardedRef, type InputHTMLAttributes, type ReactNode } from 'react'
import clsx from 'clsx'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** Mixed state (a select-all over a partial selection). Wins over `checked` visually. */
  indeterminate?: boolean
  /** Visible label beside the box. Omit it and pass `aria-label` instead. */
  label?: ReactNode
  /** Class for the label wrapper (only with `label`). */
  wrapperClassName?: string
}

const BOX =
  'peer m-0 grid h-4 w-4 flex-none cursor-pointer appearance-none place-items-center border border-text-primary bg-transparent align-middle ' +
  'checked:bg-text-primary indeterminate:bg-text-primary ' +
  "checked:after:-mt-0.5 checked:after:block checked:after:h-1 checked:after:w-2 checked:after:-rotate-45 checked:after:border-b-[1.5px] checked:after:border-l-[1.5px] checked:after:border-[color:var(--background)] checked:after:content-[''] " +
  "indeterminate:after:block indeterminate:after:h-[1.5px] indeterminate:after:w-2 indeterminate:after:rotate-0 indeterminate:after:border-0 indeterminate:after:bg-[color:var(--background)] indeterminate:after:content-[''] " +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus ' +
  'disabled:cursor-not-allowed disabled:border-[color:var(--text-placeholder)] disabled:checked:bg-[color:var(--text-placeholder)] disabled:indeterminate:bg-[color:var(--text-placeholder)]'

function assignRef<T>(ref: ForwardedRef<T>, value: T | null) {
  if (typeof ref === 'function') ref(value)
  else if (ref) ref.current = value
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { indeterminate = false, label, className, wrapperClassName, disabled, style, ...rest },
  ref,
) {
  const inner = useRef<HTMLInputElement | null>(null)
  // `indeterminate` is a DOM property with no attribute, so it is set after render.
  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate
  }, [indeterminate])

  const box = (
    <input
      ref={(el) => {
        inner.current = el
        assignRef(ref, el)
      }}
      type="checkbox"
      disabled={disabled}
      className={clsx(BOX, className)}
      style={{ borderRadius: 2, ...style }}
      {...rest}
    />
  )
  if (!label) return box
  return (
    <label className={clsx('inline-flex min-w-0 items-start gap-2', disabled ? 'cursor-not-allowed' : 'cursor-pointer', wrapperClassName)}>
      <span className="grid h-5 flex-none place-items-center">{box}</span>
      <span className={clsx('min-w-0 text-body', disabled ? 'text-text-placeholder' : 'text-text-primary')}>{label}</span>
    </label>
  )
})

export default Checkbox
